import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// Reintenta una llamada async hasta 3 veces (con espera entre intentos) antes de
// darse por vencido — para que un corte de wifi momentáneo no bote todo el envío.
async function conReintento(fn, intentos = 6, esperaMs = 2000) {
  let ultimoError;
  for (let i = 0; i < intentos; i++) {
    try { return await fn(); }
    catch (e) {
      ultimoError = e;
      // Espera creciente entre intentos (2s, 4s, 8s, 16s, 32s) — cubre cortes
      // de conexión más largos, no solo un parpadeo momentáneo.
      if (i < intentos - 1) await new Promise(r => setTimeout(r, esperaMs * Math.pow(2, i)));
    }
  }
  throw ultimoError;
}

// ─────────────────────────────────────────
// Colores por sabor (mismos que el ERP)
// ─────────────────────────────────────────
const COLORES_SABOR = {
  'Vainilla':  { bg: '#FFF8E1', fill: '#FFD54F', text: '#5D4037' },
  'Chocolate': { bg: '#EFEBE9', fill: '#6D4C41', text: '#3E2723' },
  'Fresa':     { bg: '#FCE4EC', fill: '#F06292', text: '#880E4F' },
  'Arequipe':  { bg: '#FFF3E0', fill: '#FFA726', text: '#E65100' },
  'Maracuyá':  { bg: '#FFFDE7', fill: '#FFD600', text: '#F57F17' },
  'Limón':     { bg: '#F9FBE7', fill: '#C6E84E', text: '#558B2F' },
  'Mora':      { bg: '#F3E5F5', fill: '#9C27B0', text: '#4A148C' },
  'Coco':      { bg: '#F5F5F5', fill: '#E0E0E0', text: '#424242' },
  'Mango':     { bg: '#FFF8E1', fill: '#FF8F00', text: '#E65100' },
  'Pistacho':  { bg: '#E8F5E9', fill: '#66BB6A', text: '#1B5E20' },
};
const COLOR_DEFAULT = { bg: '#EEF2FF', fill: '#818CF8', text: '#312E81' };

// ─────────────────────────────────────────
// Bandeja visual táctil (nivel 0-3)
// ─────────────────────────────────────────
function BandejaVisual({ nivel, onChange, color, disabled }) {
  const segmentos = [1, 2, 3];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <div style={{ position: 'relative', width: '100%', maxWidth: 110, background: 'rgba(255,255,255,0.5)', borderRadius: '6px 6px 14px 14px', border: '2px solid rgba(0,0,0,0.12)', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.12), inset 0 1px 3px rgba(255,255,255,0.8)' }}>
        <div style={{ height: 6, background: 'rgba(0,0,0,0.08)', borderBottom: '1px solid rgba(0,0,0,0.1)' }} />
        <div style={{ display: 'flex', height: 60 }}>
          {segmentos.map((seg) => (
            <button
              key={seg} disabled={disabled}
              onClick={() => !disabled && onChange(nivel === seg ? seg - 1 : seg)}
              style={{ flex: 1, border: 'none', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', background: seg <= nivel ? color.fill : 'rgba(255,255,255,0.4)', cursor: disabled ? 'default' : 'pointer', borderRight: seg < 3 ? '1px dashed rgba(0,0,0,0.15)' : 'none' }}
            >
              {seg <= nivel && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <div style={{ width: 18, height: 16, borderRadius: '50% 50% 40% 40%', background: color.fill, filter: 'brightness(0.85)', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                  <div style={{ width: 18, height: 16, borderRadius: '50% 50% 40% 40%', background: color.fill, filter: 'brightness(0.9)', marginTop: -4, boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                </div>
              )}
            </button>
          ))}
        </div>
        <div style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, padding: '3px 0 5px', background: 'rgba(255,255,255,0.6)', letterSpacing: '0.05em', color: color.text }}>
          {['Vacía', '⅓', '⅔', 'Llena'][nivel]}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Contador +/- de bandejas
// ─────────────────────────────────────────
function ContadorBandejas({ valor, onChange, label, icon, disabled }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textAlign: 'center' }}>{icon} {label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button disabled={disabled || valor <= 0} onClick={() => onChange(Math.max(0, valor - 1))}
          style={{ width: 32, height: 32, border: '2px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: disabled ? 'default' : 'pointer', fontSize: 17, fontWeight: 700, color: '#374151', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: (disabled || valor <= 0) ? 0.4 : 1 }}>−</button>
        <div style={{ width: 38, height: 32, background: '#F9FAFB', border: '2px solid #E5E7EB', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, color: '#111827' }}>{valor}</div>
        <button disabled={disabled} onClick={() => onChange(valor + 1)}
          style={{ width: 32, height: 32, border: '2px solid #E5E7EB', borderRadius: 8, background: 'white', cursor: disabled ? 'default' : 'pointer', fontSize: 17, fontWeight: 700, color: '#374151', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Tarjeta de sabor
// ─────────────────────────────────────────
function TarjetaSabor({ sabor, registro, onChange, modoProduccion, disabled }) {
  const color = COLORES_SABOR[sabor.nombre] || COLOR_DEFAULT;
  return (
    <div style={{ borderRadius: 14, padding: '12px 10px', position: 'relative', display: 'flex', flexDirection: 'column', gap: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', background: color.bg, border: sabor.es_premium ? '2px solid #F59E0B' : `2px solid ${color.fill}30` }}>
      {sabor.es_premium && (
        <div style={{ position: 'absolute', top: 6, right: 6, background: '#FEF3C7', color: '#92400E', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 20, border: '1px solid #F59E0B' }}>⭐</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', flexShrink: 0, background: color.fill }} />
        {sabor.nombre}
      </div>
      {modoProduccion ? (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, paddingTop: 6, flexWrap: 'wrap' }}>
          <ContadorBandejas valor={registro.bandejas_2_5kg} onChange={(v) => onChange({ ...registro, bandejas_2_5kg: v })} label="2.5kg" icon="🧊" disabled={disabled} />
          <ContadorBandejas valor={registro.bandejas_3_5kg} onChange={(v) => onChange({ ...registro, bandejas_3_5kg: v })} label="3.5kg" icon="🧊" disabled={disabled} />
          <ContadorBandejas valor={registro.galones} onChange={(v) => onChange({ ...registro, galones: v })} label="Galón" icon="🥛" disabled={disabled} />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <BandejaVisual nivel={registro.nivel_bandeja} onChange={(v) => onChange({ ...registro, nivel_bandeja: v })} color={color} disabled={disabled} />
          <ContadorBandejas valor={registro.bandejas_congelador} onChange={(v) => onChange({ ...registro, bandejas_congelador: v })} label="Congelador" icon="🧊" disabled={disabled} />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────
// Modal: solicitar corrección
// ─────────────────────────────────────────
function ModalSolicitud({ bodegaId, fecha, onClose }) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);

  const handleEnviar = async () => {
    if (!motivo.trim()) return;
    setEnviando(true);
    try {
      await API.post('/correcciones/solicitar', { tipo: 'inventario_helados', registro_id: bodegaId, bodega_id: bodegaId, fecha, motivo });
      toast.success('Solicitud enviada a administración');
      onClose();
    } catch { toast.error('Error al enviar solicitud'); }
    finally { setEnviando(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 400, padding: 24, boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
        <h3 style={{ fontSize: 17, fontWeight: 800, margin: '0 0 8px' }}>✏️ Solicitar corrección</h3>
        <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 14 }}>Explica qué necesitas corregir. Administración recibirá tu solicitud.</p>
        <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Describe el error..." rows={4}
          style={{ width: '100%', padding: '10px 14px', border: '2px solid #E5E7EB', borderRadius: 10, fontSize: 14, resize: 'none', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 14 }} />
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '11px 0', border: '2px solid #E5E7EB', borderRadius: 10, background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#374151' }}>Cancelar</button>
          <button onClick={handleEnviar} disabled={enviando || !motivo.trim()} style={{ flex: 1, padding: '11px 0', border: 'none', borderRadius: 10, background: '#F59E0B', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
            {enviando ? 'Enviando...' : '📤 Enviar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Modal: confirmar y bloquear
// ─────────────────────────────────────────
function ModalConfirmar({ saboresContados, totalSabores, onConfirmar, onCancelar, guardando }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div style={{ background: 'white', borderRadius: 16, width: '100%', maxWidth: 380, padding: 26, textAlign: 'center', boxShadow: '0 24px 64px rgba(0,0,0,0.3)' }}>
        <div style={{ fontSize: 42, marginBottom: 10 }}>🍦🔒</div>
        <h3 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 8px' }}>¿Confirmas el inventario?</h3>
        <p style={{ fontSize: 13, color: '#374151', margin: '0 0 14px', lineHeight: 1.5 }}>
          Una vez confirmado quedará bloqueado. No podrás modificarlo sin autorización de administración.
        </p>
        <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '10px 14px', marginBottom: 18, fontSize: 13, color: '#166534' }}>
          🍦 <strong>{saboresContados}</strong> de <strong>{totalSabores}</strong> sabores registrados
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onCancelar} style={{ flex: 1, padding: '11px 0', border: '2px solid #E5E7EB', borderRadius: 10, background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>Revisar de nuevo</button>
          <button onClick={onConfirmar} disabled={guardando} style={{ flex: 1, padding: '11px 0', border: 'none', borderRadius: 10, background: '#1D4ED8', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
            {guardando ? '⏳' : '🔒 Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// PANTALLA — /helados
// ─────────────────────────────────────────
export default function Helados() {
  const { usuario } = useAuth();
  // Quién puede EDITAR cualquier bodega (gerencia/administración) vs quién solo
  // puede CAMBIAR de bodega para ver su historial, sin poder tocar el
  // inventario de un PDV que no es el suyo (producción).
  const puedeEditarCualquiera = ['gerencia', 'administracion'].includes(usuario?.rol);
  const puedeVerOtrasBodegas = ['gerencia', 'administracion', 'produccion'].includes(usuario?.rol);
  const esProduccionRol = usuario?.rol === 'produccion';

  const [sabores, setSabores] = useState([]);
  const [bodegas, setBodegas] = useState([]);
  const [bodegaId, setBodegaId] = useState('');
  const [bodegaNombre, setBodegaNombre] = useState('');
  const [bodegaTipo, setBodegaTipo] = useState('');
  const [bodegaPropiaId, setBodegaPropiaId] = useState(''); // solo relevante para producción
  const [registros, setRegistros] = useState({});
  const [bloqueado, setBloqueado] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [errorPersistente, setErrorPersistente] = useState(null);
  const [guardadoOk, setGuardadoOk] = useState(false);
  const [vista, setVista] = useState('inventario'); // inventario | historial
  const [historial, setHistorial] = useState([]);
  const [registroDetalle, setRegistroDetalle] = useState(null);
  const [modalConfirm, setModalConfirm] = useState(false);
  const [modalCorreccion, setModalCorreccion] = useState(false);

  const modoProduccion = bodegaTipo === 'produccion';
  const hoy = format(new Date(), 'yyyy-MM-dd');
  // Producción viendo un PDV que no es el suyo: solo lectura del historial,
  // no puede llenar ni tocar el inventario de ese PDV.
  const soloLecturaOtroPdv = esProduccionRol && bodegaPropiaId && String(bodegaId) !== String(bodegaPropiaId);

  // Punto de venta / bodega
  useEffect(() => {
    if (puedeEditarCualquiera) {
      API.get('/bodegas').then((r) => {
        setBodegas(r.data);
        if (r.data.length) { setBodegaId(String(r.data[0].id)); setBodegaNombre(r.data[0].nombre); setBodegaTipo(r.data[0].tipo || ''); }
      }).catch(() => toast.error('No se pudieron cargar los puntos de venta'));
    } else {
      // Producción y punto_venta: cargar primero SU propia bodega (para
      // llenarla), con el tipo real que devuelve el perfil — antes se forzaba
      // 'punto_venta' para todos, por lo que producción nunca veía su propia
      // vista de bandejas por presentación.
      API.get('/app/mi-perfil').then((r) => {
        if (r.data?.bodega_id) {
          setBodegaId(String(r.data.bodega_id));
          setBodegaNombre(r.data.bodega_nombre || '');
          setBodegaTipo(r.data.bodega_tipo || 'punto_venta');
          setBodegaPropiaId(String(r.data.bodega_id));
        } else {
          toast.error('No tienes un punto de venta asignado');
        }
      }).catch(() => toast.error('Error cargando tu perfil'));
      // Producción también puede ver (no editar) el historial de los PDV,
      // para decidir qué enviarles — necesita la lista completa.
      if (puedeVerOtrasBodegas) {
        API.get('/bodegas').then((r) => setBodegas(r.data)).catch(() => {});
      }
    }
  }, []);

  const cambiarBodega = (b) => {
    setBodegaId(String(b.id)); setBodegaNombre(b.nombre); setBodegaTipo(b.tipo || '');
    // Producción cambiando a un PDV ajeno: forzar a la pestaña de historial,
    // ya que no puede llenar/editar ese inventario.
    if (esProduccionRol && String(b.id) !== String(bodegaPropiaId)) {
      setVista('historial');
    }
  };

  // Sabores
  useEffect(() => {
    API.get('/helados/sabores', { params: { bodega_id: bodegaId } }).then((r) => setSabores(r.data)).catch(() => {});
  }, [bodegaId]);

  // Inventario del día
  useEffect(() => {
    if (!bodegaId || sabores.length === 0) return;
    setCargando(true);
    API.get(`/helados/inventario/${bodegaId}`, { params: { fecha: hoy } })
      .then(({ data }) => {
        const mapa = {};
        (data.inventario || []).forEach((item) => {
          mapa[item.sabor_id] = {
            sabor_id: item.sabor_id,
            nivel_bandeja: item.nivel_bandeja,
            bandejas_congelador: item.bandejas_congelador,
            bandejas_produccion: item.bandejas_produccion,
            bandejas_2_5kg: item.bandejas_2_5kg,
            bandejas_3_5kg: item.bandejas_3_5kg,
            galones: item.galones,
          };
        });
        setRegistros(mapa);
        setBloqueado(data.bloqueado || false);
        setGuardadoOk(false);
      })
      .catch(() => toast.error('Error cargando inventario'))
      .finally(() => setCargando(false));
  }, [bodegaId, sabores.length, hoy]);

  // Historial
  useEffect(() => {
    if (!bodegaId || vista !== 'historial') return;
    API.get(`/helados/historial/${bodegaId}`).then((r) => setHistorial(r.data || [])).catch(() => {});
  }, [bodegaId, vista]);

  const verRegistro = async (fecha) => {
    try {
      const { data } = await API.get(`/helados/inventario/${bodegaId}`, { params: { fecha } });
      const conDatos = (data.inventario || []).filter((s) => s.nivel_bandeja > 0 || s.bandejas_congelador > 0 || s.bandejas_produccion > 0);
      setRegistroDetalle({ fecha, sabores: conDatos, bloqueado: data.bloqueado });
    } catch { toast.error('Error cargando el registro'); }
  };

  const getRegistro = (sabor) => registros[sabor.id] || { sabor_id: sabor.id, nivel_bandeja: 0, bandejas_congelador: 0, bandejas_produccion: 0, bandejas_2_5kg: 0, bandejas_3_5kg: 0, galones: 0 };

  const handleChange = (saborId, nuevoRegistro) => {
    if (bloqueado) return;
    setRegistros((prev) => ({ ...prev, [saborId]: nuevoRegistro }));
    setGuardadoOk(false);
  };

  const handleGuardar = async () => {
    setGuardando(true);
    setErrorPersistente(null);
    try {
      await conReintento(() => API.post('/helados/inventario', { bodega_id: bodegaId, registros: sabores.map((s) => ({ sabor_id: s.id, ...getRegistro(s) })) }));
      setGuardadoOk(true);
      toast.success('💾 Guardado como borrador');
    } catch {
      setErrorPersistente('No se pudo guardar el borrador después de varios intentos. Revisa tu conexión (wifi/datos) e inténtalo de nuevo. Si sigue sin funcionar, avisa antes de cerrar la app.');
      toast.error('Error al guardar');
    }
    finally { setGuardando(false); }
  };

  const handleConfirmarYBloquear = async () => {
    setGuardando(true);
    setErrorPersistente(null);
    try {
      await conReintento(() => API.post('/helados/inventario', { bodega_id: bodegaId, registros: sabores.map((s) => ({ sabor_id: s.id, ...getRegistro(s) })) }));
      await conReintento(() => API.post(`/helados/inventario/${bodegaId}/${hoy}/bloquear`));
      // Verificación extra: no confiar solo en que el POST respondió 200 — se
      // vuelve a preguntar al servidor si de verdad quedó bloqueado antes de
      // decirle al usuario que todo salió bien.
      const verif = await conReintento(() => API.get(`/helados/inventario/${bodegaId}`, { params: { fecha: hoy } }));
      if (!verif.data?.bloqueado) throw new Error('No se pudo confirmar el bloqueo en el servidor');
      setBloqueado(true);
      setGuardadoOk(true);
      toast.success('✅ Inventario enviado y bloqueado');
    } catch {
      setErrorPersistente('El inventario NO quedó confirmado después de varios intentos. Por favor vuelve a intentar el envío — si el problema continúa, avisa antes de cerrar la app para no perder lo que ya llenaste.');
      toast.error('Error al bloquear inventario');
    }
    finally { setGuardando(false); setModalConfirm(false); }
  };

  const saboresAgotados = sabores.filter((s) => {
    const r = getRegistro(s);
    return r.nivel_bandeja === 0 && r.bandejas_congelador === 0 && r.bandejas_produccion === 0;
  });
  const saboresContados = sabores.filter((s) => {
    const r = getRegistro(s);
    return r.nivel_bandeja > 0 || r.bandejas_congelador > 0 || r.bandejas_produccion > 0;
  }).length;

  const mostrarBarra = vista === 'inventario' && !cargando && !bloqueado && !soloLecturaOtroPdv && sabores.length > 0;

  return (
    <div style={{ paddingBottom: mostrarBarra ? 78 : 0 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#111827' }}>🍦 Helados</h1>
          <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>{bodegaNombre || '—'}</div>
        </div>
        {bloqueado && (
          <span style={{ padding: '5px 12px', borderRadius: 20, fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', background: '#D1FAE5', color: '#065F46' }}>🔒 Bloqueado</span>
        )}
      </div>

      {/* Selector bodega — admin y producción (producción solo para ver historial) */}
      {puedeVerOtrasBodegas && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, overflowX: 'auto', paddingBottom: 4 }}>
          {bodegas.map((b) => (
            <button key={b.id} onClick={() => cambiarBodega(b)} style={{ padding: '6px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, border: `2px solid ${String(bodegaId) === String(b.id) ? '#1a1a2e' : '#E5E7EB'}`, background: String(bodegaId) === String(b.id) ? '#1a1a2e' : 'white', color: String(bodegaId) === String(b.id) ? 'white' : '#374151', whiteSpace: 'nowrap', flexShrink: 0 }}>{b.nombre}</button>
          ))}
        </div>
      )}

      {/* Vista */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[{ id: 'inventario', label: '🍦 Hoy' }, { id: 'historial', label: '📋 Historial' }]
          .filter(t => t.id !== 'inventario' || !soloLecturaOtroPdv)
          .map((t) => (
          <button key={t.id} onClick={() => { setVista(t.id); setRegistroDetalle(null); }}
            style={{ padding: '6px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, border: `2px solid ${vista === t.id ? '#1a1a2e' : '#E5E7EB'}`, background: vista === t.id ? '#1a1a2e' : 'white', color: vista === t.id ? 'white' : '#374151' }}>{t.label}</button>
        ))}
      </div>

      {soloLecturaOtroPdv && (
        <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 12, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#1E40AF', fontWeight: 600 }}>
          👁️ Estás viendo el historial de {bodegaNombre} — solo lectura, para decidir qué enviar. No puedes editar el inventario de un PDV que no es el tuyo.
        </div>
      )}

      {vista === 'historial' ? (
        registroDetalle ? (
          <div style={{ background: 'white', borderRadius: 14, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 14, color: '#111827' }}>
                  {(() => { try { return format(new Date(registroDetalle.fecha + 'T12:00'), "d 'de' MMMM yyyy", { locale: es }); } catch { return registroDetalle.fecha; } })()}
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                  {registroDetalle.sabores.length} sabores con stock
                  {registroDetalle.bloqueado && <span style={{ marginLeft: 8, background: '#D1FAE5', color: '#065F46', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>🔒</span>}
                </div>
              </div>
              <button onClick={() => setRegistroDetalle(null)} style={{ background: 'none', border: 'none', color: '#2563EB', fontSize: 13, cursor: 'pointer', fontWeight: 600, padding: 0 }}>← Volver</button>
            </div>
            {registroDetalle.sabores.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: '#9CA3AF', fontSize: 13 }}>Sin stock registrado</div>
            ) : (
              registroDetalle.sabores.map((s) => (
                <div key={s.sabor_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid #F3F4F6' }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>{s.sabor || s.nombre}</div>
                  {modoProduccion ? (
                    <div style={{ display: 'flex', gap: 8, fontSize: 11, color: '#6366F1', fontWeight: 700 }}>
                      <span>2.5kg: {s.bandejas_2_5kg || 0}</span>
                      <span>3.5kg: {s.bandejas_3_5kg || 0}</span>
                      <span>Galón: {s.galones || 0}</span>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ display: 'flex', gap: 3 }}>
                        {[1, 2, 3].map((n) => <div key={n} style={{ width: 12, height: 12, borderRadius: 3, background: n <= s.nivel_bandeja ? '#3B4E8C' : '#E5E7EB' }} />)}
                      </div>
                      <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 700 }}>🧊{s.bandejas_congelador || 0}</div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        ) : historial.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9CA3AF' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
            <div style={{ fontWeight: 600 }}>Sin registros aún</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {historial.map((h, i) => (
              <button key={i} onClick={() => verRegistro(h.fecha.slice(0, 10))} style={{ background: 'white', borderRadius: 14, padding: '12px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', cursor: 'pointer', border: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', textAlign: 'left' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
                    {(() => { try { return format(new Date(h.fecha.slice(0, 10) + 'T12:00'), "d 'de' MMM", { locale: es }); } catch { return h.fecha; } })()}
                  </div>
                  <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{h.total_sabores || 0} sabores</div>
                </div>
                <span style={{ padding: '4px 10px', borderRadius: 20, fontWeight: 700, fontSize: 11, background: h.bloqueado ? '#D1FAE5' : '#FEF3C7', color: h.bloqueado ? '#065F46' : '#92400E' }}>
                  {h.bloqueado ? '🔒 Bloqueado' : '📝 Borrador'}
                </span>
              </button>
            ))}
          </div>
        )
      ) : cargando ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>Cargando...</div>
      ) : (
        <>
          {errorPersistente && (
            <div style={{ background: '#FEF2F2', border: '2px solid #EF4444', borderRadius: 12, padding: '14px 16px', marginBottom: 14, fontSize: 13, color: '#991B1B', fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <span>⚠️ {errorPersistente}</span>
              <button onClick={() => setErrorPersistente(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#991B1B', flexShrink: 0 }}>✕</button>
            </div>
          )}

          {bloqueado && (
            <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: 12, padding: '12px 16px', marginBottom: 14, fontSize: 13, color: '#065F46', fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span>✅ Inventario de hoy ya confirmado</span>
              {!puedeEditarCualquiera && (
                <button onClick={() => setModalCorreccion(true)} style={{ padding: '6px 12px', border: '1px solid #F59E0B', borderRadius: 8, background: 'white', color: '#92400E', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>✏️ Solicitar corrección</button>
              )}
            </div>
          )}

          {saboresAgotados.length > 0 && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 12, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#991B1B', fontWeight: 600 }}>
              ⚠️ Sin stock: {saboresAgotados.map((s) => s.nombre).join(', ')}
            </div>
          )}

          {sabores.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9CA3AF' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🍦</div>
              <div style={{ fontWeight: 600 }}>No hay sabores configurados</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {sabores.map((sabor) => (
                <TarjetaSabor key={sabor.id} sabor={sabor} registro={getRegistro(sabor)} onChange={(nuevo) => handleChange(sabor.id, nuevo)} modoProduccion={modoProduccion} disabled={bloqueado || soloLecturaOtroPdv} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Barra de acción fija */}
      {mostrarBarra && (
        <div style={{ position: 'fixed', left: 0, right: 0, bottom: 'calc(70px + env(safe-area-inset-bottom, 0px))', display: 'flex', gap: 10, padding: '10px 20px', background: 'white', borderTop: '1px solid #E5E7EB', boxShadow: '0 -2px 10px rgba(0,0,0,0.06)', zIndex: 90 }}>
          <button onClick={handleGuardar} disabled={guardando}
            style={{ flex: 1, padding: '13px 0', border: '2px solid #E5E7EB', borderRadius: 12, background: guardadoOk ? '#D1FAE5' : 'white', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: guardadoOk ? '#065F46' : '#374151' }}>
            {guardando ? '⏳' : guardadoOk ? '✓ Borrador' : '💾 Borrador'}
          </button>
          <button onClick={() => setModalConfirm(true)} disabled={guardando}
            style={{ flex: 2, padding: '13px 0', border: 'none', borderRadius: 12, background: '#1D4ED8', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
            📤 Confirmar y bloquear
          </button>
        </div>
      )}

      {modalConfirm && (
        <ModalConfirmar saboresContados={saboresContados} totalSabores={sabores.length} guardando={guardando} onConfirmar={handleConfirmarYBloquear} onCancelar={() => setModalConfirm(false)} />
      )}
      {modalCorreccion && (
        <ModalSolicitud bodegaId={bodegaId} fecha={hoy} onClose={() => setModalCorreccion(false)} />
      )}
    </div>
  );
}
